import { TIME_INDEX_META, type BirthCalendar, type BirthTimeCertainty } from './birthInput.js';

export type BirthInputFieldId = 'calendar' | 'date' | 'timeIndex' | 'gender' | 'isLeapMonth' | 'certainty';

export interface BirthInputOptionViewModel {
  value: string;
  label: string;
  description?: string;
}

export interface BirthInputFieldViewModel {
  id: BirthInputFieldId;
  label: string;
  control: 'segmented' | 'text' | 'select' | 'checkbox';
  required: boolean;
  personalData: boolean;
  inputMode?: 'numeric';
  description: string;
  options?: readonly BirthInputOptionViewModel[];
}

export interface BirthInputFormViewModel {
  calendar: BirthCalendar;
  fields: readonly BirthInputFieldViewModel[];
  collectedFieldIds: readonly BirthInputFieldId[];
  omittedIdentityFields: readonly string[];
  disclosure: string;
}

export const BIRTH_INPUT_ALLOWED_FIELDS: readonly BirthInputFieldId[] = Object.freeze([
  'calendar', 'date', 'timeIndex', 'gender', 'isLeapMonth', 'certainty',
]);

export const BIRTH_INPUT_FORBIDDEN_IDENTITY_FIELDS = Object.freeze([
  'name', 'email', 'phone', 'address', 'account', 'precise_location',
] as const);

const CERTAINTY_OPTIONS: readonly BirthInputOptionViewModel[] = Object.freeze([
  Object.freeze({ value: 'exact', label: '確定', description: '出生時辰可視為確定。' }),
  Object.freeze({ value: 'approximate', label: '大約', description: '時間接近某一時辰，建議搭配相鄰時辰比較。' }),
  Object.freeze({ value: 'unknown_within_window', label: '不確定', description: '只知道大致時間範圍，建議使用候選時辰比較。' }),
]);

function timeOptions(): readonly BirthInputOptionViewModel[] {
  return Object.freeze(TIME_INDEX_META.map((item) => Object.freeze({
    value: String(item.index),
    label: item.label,
    description: `${item.range}｜${item.branch}時${item.lateRat ? '｜晚子時需留意換日規則' : ''}`,
  })));
}

export function buildBirthInputFormViewModel(calendar: BirthCalendar): BirthInputFormViewModel {
  if (calendar !== 'solar' && calendar !== 'lunar') throw new Error(`Unsupported birth input calendar ${String(calendar)}`);
  const fields: BirthInputFieldViewModel[] = [
    Object.freeze({
      id: 'calendar', label: '曆法', control: 'segmented', required: true, personalData: false,
      description: '選擇陽曆或農曆；兩者會經同一排盤 Ruleset 正規化。',
      options: Object.freeze([
        Object.freeze({ value: 'solar', label: '陽曆' }), Object.freeze({ value: 'lunar', label: '農曆' }),
      ]),
    }),
    Object.freeze({
      id: 'date', label: calendar === 'solar' ? '出生日期（陽曆）' : '出生日期（農曆）', control: 'text', required: true, personalData: true, inputMode: 'numeric',
      description: '格式 YYYY-M-D。正式 Standard 模式另受已驗證年代範圍限制。',
    }),
    Object.freeze({
      id: 'timeIndex', label: '出生時辰', control: 'select', required: true, personalData: true,
      description: '使用 0～12 的明確時辰索引；早子與晚子分開，不做隱性環狀換算。', options: timeOptions(),
    }),
    Object.freeze({
      id: 'gender', label: '排盤性別參數', control: 'segmented', required: true, personalData: true,
      description: '目前排盤引擎需要此參數決定部分運限方向；只作計算用途，不作社會身分或價值判斷。',
      options: Object.freeze([
        Object.freeze({ value: 'male', label: '男' }), Object.freeze({ value: 'female', label: '女' }),
      ]),
    }),
    ...(calendar === 'lunar' ? [Object.freeze({
      id: 'isLeapMonth' as const, label: '是否為閏月', control: 'checkbox' as const, required: false, personalData: true,
      description: '只有農曆輸入顯示；閏月前後半的計算差異由固定 Ruleset 與 Regression 管理。',
    })] : []),
    Object.freeze({
      id: 'certainty', label: '出生時間確定度', control: 'segmented', required: false, personalData: false,
      description: '用來決定是否提示時辰敏感度比較；不轉換成命盤正確率或機率。', options: CERTAINTY_OPTIONS,
    }),
  ];
  return Object.freeze({
    calendar,
    fields: Object.freeze(fields),
    collectedFieldIds: Object.freeze(fields.map((field) => field.id)),
    omittedIdentityFields: BIRTH_INPUT_FORBIDDEN_IDENTITY_FIELDS,
    disclosure: '只收集排盤必要的出生資料；核心表單不要求姓名、Email、電話、地址、帳號或精確位置。出生資料在正式 V1 中只於瀏覽器本機處理。',
  });
}

export function assertBirthInputFormViewModel(view: BirthInputFormViewModel): void {
  const ids = new Set(view.collectedFieldIds);
  for (const id of ids) if (!BIRTH_INPUT_ALLOWED_FIELDS.includes(id)) throw new Error(`Birth input form contains unapproved field ${id}`);
  for (const forbidden of BIRTH_INPUT_FORBIDDEN_IDENTITY_FIELDS) {
    if ((view.collectedFieldIds as readonly string[]).includes(forbidden)) throw new Error(`Birth input form must not collect identity field ${forbidden}`);
  }
  for (const required of ['calendar', 'date', 'timeIndex', 'gender'] as const) if (!ids.has(required)) throw new Error(`Birth input form missing required field ${required}`);
  if (view.calendar === 'solar' && ids.has('isLeapMonth')) throw new Error('Solar birth input form must not show leap-month control');
  if (view.calendar === 'lunar' && !ids.has('isLeapMonth')) throw new Error('Lunar birth input form must expose leap-month control');
  const timeField = view.fields.find((field) => field.id === 'timeIndex');
  if (timeField?.options?.length !== 13) throw new Error('Birth time control must expose all 13 explicit time indexes');
  if (!timeField.options.some((option) => option.value === '0') || !timeField.options.some((option) => option.value === '12')) throw new Error('Birth time control must preserve early/late Rat indexes');
}

export function birthTimeCertaintyLabel(value: BirthTimeCertainty): string {
  return CERTAINTY_OPTIONS.find((option) => option.value === value)?.label ?? '未知';
}
